import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { BAND_CONTENT, DIMENSION_LABEL, DIMENSION_ORDER, SCORED_QUESTIONS, TOTAL_QUESTIONS, dimensionLevel } from './scoring';
import { useScore } from './ScoreContext';
export function Diagnostic() {
  const {
    answers,
    setAnswer,
    answeredCount,
    result,
    isDiagnosticOpen,
    closeDiagnostic
  } = useScore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const firstUnansweredIndex = useMemo(() => {
    const index = SCORED_QUESTIONS.findIndex(question => answers[question.id] === undefined);
    return index === -1 ? 0 : index;
  }, [answers]);
  useEffect(() => {
    if (isDiagnosticOpen) {
      setCurrentIndex(firstUnansweredIndex);
    }
  }, [isDiagnosticOpen, firstUnansweredIndex]);
  if (!isDiagnosticOpen) return null;
  if (result) {
    const content = BAND_CONTENT[result.band];
    return <div className="fixed inset-0 z-[60] overflow-y-auto bg-paper px-6 py-8 md:py-12" role="dialog" aria-modal="true" aria-labelledby="score-result-title" data-id="element-50">
        <div className="mx-auto max-w-4xl" data-id="element-51">
          <div className="flex justify-end" data-id="element-52">
            <button type="button" onClick={closeDiagnostic} className="rounded-full p-3 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close score results" data-id="element-53">
              <X className="h-5 w-5" data-id="element-54" />
            </button>
          </div>

          <main className="pb-12 text-center" data-id="element-55">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-56">
              Your Reader-to-Revenue Score
            </p>
            <p className="mt-4 text-sm font-medium text-text-muted" data-id="element-57">
              {result.totalScore} of 24 points
            </p>
            <h1 id="score-result-title" className="mx-auto mt-5 max-w-3xl font-serif text-4xl leading-tight text-navy md:text-6xl" data-id="element-58">
              {result.band}
            </h1>
            <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-59">
              {content.acknowledgment}
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-60">
              {content.meaning}
            </p>

            <div className="mt-12 grid gap-5 text-left md:grid-cols-2" data-id="element-61">
              <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-62">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-63">
                  Biggest opportunity
                </p>
                <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-64">
                  {DIMENSION_LABEL[result.biggestOpportunity]}
                </h2>
                <p className="mt-4 leading-relaxed text-text-muted" data-id="element-65">
                  {content.opportunity}
                </p>
              </article>

              <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-66">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-67">
                  Strongest layer
                </p>
                <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-68">
                  {DIMENSION_LABEL[result.strongestLayer]}
                </h2>
                <p className="mt-4 leading-relaxed text-text-muted" data-id="element-69">
                  {content.nextStep}
                </p>
              </article>
            </div>

            <section className="mt-10 text-left" data-id="element-70">
              <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-71">
                Your four layers
              </p>
              <div className="grid gap-3 sm:grid-cols-2" data-id="element-72">
                {DIMENSION_ORDER.map(dimension => <div key={dimension} className="rounded-2xl border border-gray-200 bg-white px-5 py-4" data-id="element-73">
                    <div className="flex items-baseline justify-between gap-4" data-id="element-74">
                      <span className="font-semibold text-navy" data-id="element-75">
                        {DIMENSION_LABEL[dimension]}
                      </span>
                      <span className="text-sm font-medium text-text-muted" data-id="element-76">
                        {result.dimensions[dimension]} / 6
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-text-muted" data-id="element-77">
                      {dimensionLevel(result.dimensions[dimension])}
                    </p>
                  </div>)}
              </div>
            </section>

            <div className="mt-12 rounded-3xl bg-navy px-7 py-10 text-paper md:px-12" data-id="element-78">
              <h2 className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-79">
                See what a clearer path could unlock.
              </h2>
              <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-80">
                {content.ctaHelper}
              </p>
              <button type="button" onClick={closeDiagnostic} className="mt-7 inline-flex min-h-[56px] items-center justify-center rounded-xl bg-sand px-8 py-4 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus:ring-2 focus:ring-white" data-id="element-81">
                Explore a Reader Path Audit
                <ArrowRight className="ml-2 h-5 w-5" data-id="element-82" />
              </button>
            </div>
          </main>
        </div>
      </div>;
  }
  const question = SCORED_QUESTIONS[currentIndex];
  const selectedScore = answers[question.id];
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < TOTAL_QUESTIONS - 1;
  const selectAnswer = (score: number) => {
    setAnswer(question.id, score);
    if (canGoForward) {
      window.setTimeout(() => setCurrentIndex(index => index + 1), 180);
    }
  };
  return <div className="fixed inset-0 z-[60] overflow-y-auto bg-paper px-6 py-6 md:py-10" role="dialog" aria-modal="true" aria-labelledby="diagnostic-title" data-id="element-83">
      <div className="mx-auto max-w-3xl" data-id="element-84">
        <header className="flex items-center justify-between gap-4" data-id="element-85">
          <div data-id="element-86">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-87">
              Reader-to-Revenue Score
            </p>
            <p className="mt-1 text-sm font-medium text-text-muted" data-id="element-88">
              {answeredCount} of {TOTAL_QUESTIONS} answered
            </p>
          </div>
          <button type="button" onClick={closeDiagnostic} className="rounded-full p-3 text-text-muted transition-colors hover:bg-paper-dark hover:text-navy focus:outline-none focus:ring-2 focus:ring-seaglass" aria-label="Close diagnostic" data-id="element-89">
            <X className="h-5 w-5" data-id="element-90" />
          </button>
        </header>

        <div className="mt-6 flex gap-1.5" aria-hidden="true" data-id="element-91">
          {SCORED_QUESTIONS.map(item => <span key={item.id} className={`h-1.5 flex-1 rounded-full ${answers[item.id] === undefined ? 'bg-gray-200' : 'bg-seaglass'}`} data-id="element-92" />)}
        </div>

        <main className="py-12 md:py-16" data-id="element-93">
          <p className="text-sm font-medium text-text-muted" data-id="element-94">
            Question {currentIndex + 1} of {TOTAL_QUESTIONS}
          </p>
          <h1 id="diagnostic-title" className="mt-4 max-w-3xl font-serif text-3xl leading-tight text-navy md:text-5xl" data-id="element-95">
            {question.prompt}
          </h1>

          <div className="mt-9 flex flex-col gap-3" data-id="element-96">
            {question.options.map(option => {
            const isSelected = selectedScore === option.score;
            return <button key={option.letter} type="button" aria-pressed={isSelected} onClick={() => selectAnswer(option.score)} className={`flex min-h-[72px] w-full items-start gap-4 rounded-2xl border p-5 text-left transition-all focus:outline-none focus:ring-2 focus:ring-seaglass md:p-6 ${isSelected ? 'border-seaglass bg-seaglass/10 ring-1 ring-seaglass/40' : 'border-gray-200 bg-white hover:border-seaglass/50'}`} data-id="element-97">
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${isSelected ? 'border-seaglass bg-seaglass text-white' : 'border-gray-300 text-navy'}`} data-id="element-98">
                    {isSelected ? <Check className="h-4 w-4" data-id="element-99" /> : option.letter}
                  </span>
                  <span className="leading-relaxed text-text" data-id="element-100">
                    {option.text}
                  </span>
                </button>;
          })}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4" data-id="element-101">
            <button type="button" onClick={() => setCurrentIndex(index => index - 1)} disabled={!canGoBack} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-102">
              <ArrowLeft className="h-4 w-4" data-id="element-103" />
              Back
            </button>
            <button type="button" onClick={() => setCurrentIndex(index => index + 1)} disabled={!canGoForward} className="inline-flex items-center gap-2 rounded-xl px-4 py-3 font-semibold text-navy transition-colors hover:bg-paper-dark disabled:cursor-not-allowed disabled:opacity-40" data-id="element-104">
              Next question
              <ArrowRight className="h-4 w-4" data-id="element-105" />
            </button>
          </div>
        </main>
      </div>
    </div>;
}